/**
 * Filter Utilities
 *
 * Provides consistent filter processing functions across all dashboard types
 * to handle "all" value filtering and standardize multi-select behavior.
 *
 * @purpose Dashboard filter normalizers — collapse `"all"` sentinel values, coerce single/array shapes for API params, and resolve global time-filter presets (24h/7d/30d/…) to absolute ranges. Use before sending filter state to APIs.
 */

import type { GlobalTimeFilter } from '../types/filterTypes'
import {
  TIME_FILTER_PRESET_LABELS,
  TIME_FILTER_PRESET_OFFSETS_MS,
} from '../types/filterTypes'
import type {
  FilterOption,
  InventoryFilterConfig,
} from '../types/inventoryPageTypes'

/**
 * Removes "all" values from an array, returning empty array if "all" is present
 * This standardizes the behavior where selecting "all" means "show everything"
 * rather than sending an "all" value to the API.
 *
 * Based on the working pattern from EventsFilterContext.tsx
 *
 * @param values - Array of filter values that may contain "all"
 * @returns Empty array if "all" was present, otherwise the original array unchanged
 */
export const removeAllValues = (
  values: string[] | undefined | null,
): string[] => {
  if (!values || !Array.isArray(values)) {
    return []
  }

  // If 'all' is in the array, treat it as empty (show all)
  return values.includes('all') ? [] : values
}

/**
 * Process filter value for API parameters - converts single values to arrays
 * Use this when preparing filter values for API calls that expect array parameters
 *
 * @param value - The filter value (string, array, or other)
 * @returns Array of strings (empty array when cleared/invalid, single-item array for valid strings)
 */
export const processFilterValueToArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    // For arrays, remove "all" values
    return removeAllValues(value)
  } else if (value === 'all' || value === 'any') {
    // Single "all"/"any" value should clear the filter
    return []
  } else if (typeof value === 'string' && value.length > 0) {
    // Single non-empty string value becomes array
    return [value]
  }

  // Default to empty array for null, undefined, empty string
  return []
}

/**
 * Process filter values object to handle "all" clearing consistently
 * This replicates the EventsFilterContext logic for infrastructure dashboards
 *
 * @param filterValues - Object containing filter key-value pairs
 * @returns Processed filter values with "all" handling applied
 */
export const processAllFilterValues = (
  filterValues: Record<string, string | string[]>,
): Record<string, string | string[]> => {
  const processed: Record<string, string | string[]> = {}

  Object.entries(filterValues).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // If 'all' or 'any' is in the array, treat it as empty (show all)
      processed[key] =
        value.includes('all') || value.includes('any') ? [] : value
    } else if (value === 'all' || value === 'any') {
      // Single 'all'/'any' value should clear the filter
      processed[key] = []
    } else {
      // Keep other values as-is
      processed[key] = value
    }
  })

  return processed
}

/**
 * Process filter value for UI state - preserves original format (string stays string)
 * Use this when handling filter values in UI components that need to maintain data type
 *
 * @param value - The filter value (string or array)
 * @returns Processed value with "all"/"any" handling applied, preserves original format
 */
export const processFilterValueForUI = (
  value: string | string[],
): string | string[] => {
  if (Array.isArray(value)) {
    // If 'all' or 'any' is in the array, treat it as empty (show all)
    return value.includes('all') || value.includes('any') ? [] : value
  } else if (value === 'all' || value === 'any') {
    // Single 'all'/'any' value should clear the filter
    return []
  } else {
    // Keep other values as-is (strings remain strings)
    return value
  }
}

// Backward compatibility exports
// TODO: Deprecate in favor of the more descriptive names above
export const processSingleFilterValue = processFilterValueForUI

const fmtDate = (d: Date, timeZone?: string): string =>
  d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  })

/**
 * Returns a human-readable label for the active global time filter.
 * Shifted presets show the computed absolute range; custom ranges show start–end.
 * Pass timezoneMode='utc' to display times in UTC instead of browser local timezone.
 */
export const resolvedTimeFilterLabel = (
  tf: GlobalTimeFilter,
  timezoneMode: 'utc' | 'local' = 'local',
): string => {
  const tz = timezoneMode === 'utc' ? 'UTC' : undefined
  if (tf.mode === 'none') return ''
  if (tf.mode === 'preset') {
    const shift = tf.shift ?? 0
    if (shift === 0) return TIME_FILTER_PRESET_LABELS[tf.preset]
    const duration = TIME_FILTER_PRESET_OFFSETS_MS[tf.preset]
    const now = Date.now()
    const start = new Date(now - (shift + 1) * duration)
    const end = new Date(now - shift * duration)
    return `${fmtDate(start, tz)} – ${fmtDate(end, tz)}`
  }
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: tz,
    })
  return `${fmt(tf.start)} – ${fmt(tf.end)}`
}

const RISK_CATEGORY_ORDER = ['critical', 'high', 'medium', 'low', 'minimal']

/**
 * Sorts risk category strings in severity order: Critical → High → Medium → Low → Minimal.
 * Unknown values are sorted to the end alphabetically.
 */
export const sortRiskCategories = (categories: string[]): string[] =>
  [...categories].sort((a, b) => {
    const ai = RISK_CATEGORY_ORDER.indexOf(a.toLowerCase())
    const bi = RISK_CATEGORY_ORDER.indexOf(b.toLowerCase())
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })

/**
 * Descriptor for a single facet field used by buildFacetFilters.
 * - key   : property name in the raw facet object (e.g. 'providers', 'regions')
 * - id    : filter id / field name used in the UI (e.g. 'provider', 'region')
 * - label : human-readable label shown in the filter UI
 */
export type FacetDescriptor = { key: string; id: string; label: string }

/**
 * Build an InventoryFilterConfig[] from a raw server-side facet object.
 *
 * For each descriptor, a filter is only included when the corresponding raw
 * array contains more than one option (single-option facets are not useful
 * as filters). All produced filters are multi-select with 'all' as the
 * default value.
 *
 * Usage (replaces repetitive if-push blocks in normalizeFacets):
 *   return buildFacetFilters(raw, [
 *     { key: 'providers', id: 'provider', label: 'Provider' },
 *     { key: 'regions',   id: 'region',   label: 'Region'   },
 *   ])
 */
export const buildFacetFilters = (
  raw: Record<string, unknown>,
  descriptors: FacetDescriptor[],
): InventoryFilterConfig[] =>
  descriptors.flatMap(({ key, id, label }) => {
    const options = raw[key] as FilterOption[] | undefined
    if (!options || options.length <= 1) return []
    return [
      {
        id,
        field: id,
        label,
        multiple: true as const,
        options,
        defaultValue: 'all',
      },
    ]
  })
